import React from "react";
import { ChevronDown } from "lucide-react";

/**
 * @param {{ shipmentListStyle?: boolean }} props
 */
const TableSecondary = ({
  headers = [],
  data = [],
  renderRow,
  columnWidths,
  shipmentListStyle = false,
  onSort,
  sortBy,
  ascending = true,
  sortKeyByHeader,
}) => {
  const cols = headers.length || 1;

  // If caller didn't pass widths, make them equal
  const widths = columnWidths && columnWidths.length === cols
    ? columnWidths
    : Array.from({ length: cols }, () => `${100 / cols}%`);

  const headerThClass = shipmentListStyle
    ? "whitespace-nowrap border-b border-gray-300 bg-gray-100 px-2 py-2 text-left text-[11px] font-semibold text-gray-700 dark:border-acs-dark-border dark:bg-[#1a1c23] dark:text-acs-dark-text sm:px-3 sm:text-xs md:text-sm transition-none"
    : "whitespace-nowrap border-b border-gray-300 bg-gray-100 px-2 py-2 text-left text-[11px] font-semibold text-black dark:border-acs-dark-border dark:bg-[#1a1c23] dark:text-acs-dark-text sm:px-3 sm:text-xs md:text-sm transition-none";

  const bodyRowClass = shipmentListStyle
    ? "border-b border-gray-200 transition-none hover:bg-gray-100 dark:border-acs-dark-border dark:hover:bg-acs-dark-hover cursor-default text-gray-800 dark:text-acs-dark-text [&>td]:py-1.5 [&>td]:text-[11px] sm:[&>td]:text-xs md:[&>td]:text-sm"
    : "border-b border-gray-200 transition-none hover:bg-gray-100 dark:border-acs-dark-border dark:hover:bg-acs-dark-hover";

  return (
    <div className="w-full overflow-x-auto rounded-lg border border-gray-200 bg-white dark:border-acs-dark-border dark:bg-acs-dark-surface transition-none">
      <table className="min-w-full table-fixed border-collapse bg-white font-roboto dark:bg-acs-dark-surface transition-none">
        {/* Colgroup forces same widths for <th> and <td> */}
        <colgroup>
          {widths.map((w, i) => (
            <col key={i} style={{ width: w }} />
          ))}
        </colgroup>

        <thead className={`sticky top-0 z-[10] shadow-md transition-none dark:bg-acs-dark-surface ${shipmentListStyle ? "bg-gray-100" : "bg-white dark:bg-[#14151a]"}`}>
          <tr>
            {headers.map((header, idx) => {
              const isTextHeader =
                typeof header === "string" || typeof header === "number";
              const label = isTextHeader ? String(header) : "";
              const sortKey =
                isTextHeader && typeof sortKeyByHeader === "function"
                  ? sortKeyByHeader(label)
                  : null;
              const canSort = Boolean(sortKey && typeof onSort === "function");
              const isActive = canSort && sortBy === sortKey;

              return (
                <th key={idx} className={headerThClass}>
                  <div className="flex items-center gap-1">
                    {isTextHeader ? (
                      <span className="truncate">{label}</span>
                    ) : (
                      header
                    )}
                    {canSort ? (
                      <button
                        type="button"
                        onClick={() => onSort(label)}
                        className={`shrink-0 transition-none ${
                          isActive ? "opacity-100" : "opacity-50 hover:opacity-80"
                        }`}
                        aria-label={`Sort by ${label}`}
                      >
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-150 ${
                            isActive && ascending ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                    ) : null}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>

        <tbody>
          {data.length > 0 ? (
            data.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={bodyRowClass}
              >
                {renderRow(row, rowIndex)}
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={headers.length}
                className="px-fluid-3 py-fluid-4 text-center text-fluid-sm text-gray-500 dark:text-acs-dark-subtext"
              >
                No data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TableSecondary;
